import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { logErrorToDatabase } from "@/hooks/useErrorLogger";
import type { Tables } from "@/integrations/supabase/types";

type Restaurant = Tables<"restaurants">;

interface NfceEmissionResult {
  success: boolean;
  status: "authorized" | "rejected" | "pending" | "error";
  nfce_id?: string;
  numero?: number;
  serie?: number;
  chave_acesso?: string;
  protocolo?: string;
  message?: string;
  error?: string;
  cstat?: string; // Código de status da SEFAZ (ex: 100, 531, etc.)
  xmotivo?: string; // Descrição do motivo da SEFAZ
  printed?: boolean; // Se o DANFE foi impresso automaticamente
  print_error?: string; // Erro de impressão (se houver)
}

interface PaymentData {
  method: string;
  amount: number;
}

interface DestinatarioData {
  cpf?: string;
  nome?: string;
}

// Validate emitter data before emission
function validateEmitterData(restaurant: Restaurant): string[] {
  const errors: string[] = [];
  const r = restaurant as any;
  
  if (!r.cnpj) errors.push("CNPJ");
  if (!r.ie) errors.push("Inscrição Estadual");
  if (!r.razao_social) errors.push("Razão Social");
  if (!r.codigo_municipio) errors.push("Código do Município");
  if (!r.uf) errors.push("UF");
  if (!r.address) errors.push("Endereço");
  if (!r.cep) errors.push("CEP");
  if (!r.bairro) errors.push("Bairro");
  if (!r.numero_endereco) errors.push("Número do Endereço");
  if (!r.crt) errors.push("CRT");
  
  return errors;
}

export function useNfceEmission() {
  const { activeRestaurant } = useRestaurantContext();
  const [isEmitting, setIsEmitting] = useState(false);
  const [lastResult, setLastResult] = useState<NfceEmissionResult | null>(null);

  const emitNfce = useCallback(async (
    orderId: string,
    payments?: PaymentData[],
    destinatario?: DestinatarioData
  ): Promise<NfceEmissionResult> => {
    if (!activeRestaurant?.id) {
      return {
        success: false,
        status: "error",
        error: "Restaurante não selecionado",
      };
    }

    // Validate emitter data before proceeding
    const missingFields = validateEmitterData(activeRestaurant);
    if (missingFields.length > 0) {
      const errorMsg = `Dados fiscais incompletos: ${missingFields.join(", ")}`;
      toast.error("Dados fiscais incompletos", {
        description: `Preencha os campos: ${missingFields.join(", ")} em Fiscal > Emitente`,
      });
      const result: NfceEmissionResult = {
        success: false,
        status: "error",
        error: errorMsg,
      };
      setLastResult(result);
      return result;
    }

    setIsEmitting(true);
    setLastResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("emit-nfce-acbr", {
        body: {
          order_id: orderId,
          restaurant_id: activeRestaurant.id,
          payments,
          destinatario,
        },
      });

      if (error) {
        console.error("[useNfceEmission] Erro na chamada:", error);
        const result: NfceEmissionResult = {
          success: false,
          status: "error",
          error: error.message || "Erro ao emitir NFC-e",
        };
        setLastResult(result);
        toast.error("Erro ao emitir NFC-e", { description: error.message });
        
        // Log error to database
        await logErrorToDatabase(
          { code: 'NFCE_EMISSION_ERROR', message: error.message },
          'edge_function',
          activeRestaurant?.id,
          { orderId, action: 'emit_nfce' }
        );
        
        return result;
      }

      const result: NfceEmissionResult = {
        success: data.success,
        status: data.status || "error",
        nfce_id: data.nfce_id,
        numero: data.numero,
        serie: data.serie,
        chave_acesso: data.chave_acesso,
        protocolo: data.protocolo,
        message: data.message,
        error: data.error,
        cstat: data.cstat,
        xmotivo: data.xmotivo,
        printed: data.printed,
        print_error: data.print_error,
      };

      setLastResult(result);

      if (result.success) {
        toast.success("NFC-e emitida com sucesso!", {
          description: `Número: ${result.numero} | Série: ${result.serie}`,
        });
      } else {
        toast.error("Falha na emissão da NFC-e", {
          description: result.message || result.error,
        });
        
        // Log failure to database
        await logErrorToDatabase(
          { code: 'NFCE_EMISSION_REJECTED', message: result.message || result.error || 'Rejeição da SEFAZ' },
          'edge_function',
          activeRestaurant?.id,
          { orderId, action: 'emit_nfce', status: result.status }
        );
      }

      return result;
    } catch (err) {
      console.error("[useNfceEmission] Exceção:", err);
      const result: NfceEmissionResult = {
        success: false,
        status: "error",
        error: err instanceof Error ? err.message : "Erro desconhecido",
      };
      setLastResult(result);
      toast.error("Erro ao emitir NFC-e");
      
      // Log exception to database
      await logErrorToDatabase(
        { code: 'NFCE_EMISSION_EXCEPTION', message: err instanceof Error ? err.message : 'Erro desconhecido' },
        'edge_function',
        activeRestaurant?.id,
        { orderId, action: 'emit_nfce' }
      );
      
      return result;
    } finally {
      setIsEmitting(false);
    }
  }, [activeRestaurant?.id]);

  const testAcbrConnection = useCallback(async (
    host: string,
    port: number,
    timeoutMs?: number
  ): Promise<{ success: boolean; online?: boolean; message?: string; error?: string; handshake?: string; commandResponse?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("test-acbr-connection", {
        body: {
          host,
          port,
          timeout_ms: timeoutMs || 5000,
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return data;
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : "Erro de conexão",
      };
    }
  }, []);

  return {
    emitNfce,
    testAcbrConnection,
    isEmitting,
    lastResult,
  };
}
